import { addCurrency, roundCurrency } from "../utils/currencyUtils";

/**
 * Percentage table fill for org payout tables.
 * Optional prize per entry and last-place floor. Entries for a
 * column are implied from places paid and the payout ratio.
 *
 * standard: USBC sample shape (1st ~30% at 15 places, 2nd = half of 1st)
 * first: more money at the top
 * even: flatter (first about 6x last)
 */

const FIRST_LAST_RATIO = {
  standard: 15,
  even: 6,
};

// USBC Tournament Managers Manual 2026, Sample Payout Chart (15 places, $10,000).
const USBC_SAMPLE_15 = [
  0.3014, 0.1507, 0.1005, 0.0753, 0.0603, 0.0502, 0.0431, 0.0377, 0.0335,
  0.0301, 0.0274, 0.0251, 0.0232, 0.0215, 0.0201,
];

export const PAYOUT_SHAPES = {
  standard: {
    id: "standard",
    name: "Standard",
    blurb: "Close to the USBC sample chart. First about 30% when 15 places are paid. Second is half of first.",
  },
  first: {
    id: "first",
    name: "More to first",
    blurb: "Same shape as standard, with more on first. About 38% when 15 places are paid. Second is half of first.",
  },
  even: {
    id: "even",
    name: "More even",
    blurb: "Flatter. More of the fund sits in the middle and last paid places.",
  },
};

export const EASY_PLACES = 30;
export const DEFAULT_RATIO_NUMERATOR = 1;
export const DEFAULT_RATIO_DENOMINATOR = 6;
const PERCENT_SCALE = 10000;
export const USBC_FIRST_CAP_ENTRIES = 100;
export const FIRST_PLACE_CAP = 0.4;
export const DEFAULT_TYPICAL_ENTRIES = 35;
export const DEFAULT_PRIZE_PER_ENTRY = 50;
export const DEFAULT_LAST_PLACE_DOLLARS = 50;

export const RECOMMENDED_PAYOUT_OPTIONS = {
  places: String(EASY_PLACES),
  shape: "standard",
  minLastPercent: "",
  ratioNumerator: String(DEFAULT_RATIO_NUMERATOR),
  ratioDenominator: String(DEFAULT_RATIO_DENOMINATOR),
};

export const DEFAULT_PAYOUT_PLANNING = {
  prizePerEntry: "",
  lastPlaceDollars: "",
  roundToWholeDollars: true,
};

const planningFieldFilled = (value) =>
  value != null && String(value).trim() !== "";

const moneyToInput = (value) => {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(n);
};

export const isWholePositiveDollar = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && Math.abs(n - Math.round(n)) < 1e-9;
};

export const sanitizeWholeDollars = (value) => {
  if (value == null || String(value).trim() === "") return "";
  const raw = String(value).trim();
  if (/^\d+$/.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "";
  return String(Math.round(n));
};

export const recipeFromPayoutTable = (table) => {
  if (table == null || table.placesToCover == null) return null;
  return {
    options: {
      places: String(table.placesToCover),
      shape: PAYOUT_SHAPES[table.curve] ? table.curve : "standard",
      minLastPercent: "",
      ratioNumerator: String(
        table.payoutRatioNumerator ?? DEFAULT_RATIO_NUMERATOR
      ),
      ratioDenominator: String(
        table.payoutRatioDenominator ?? DEFAULT_RATIO_DENOMINATOR
      ),
    },
    planning: {
      prizePerEntry: moneyToInput(table.prizePerEntry),
      lastPlaceDollars: sanitizeWholeDollars(table.lastPlaceMustGet),
      roundToWholeDollars: table.roundToWholeDollars !== false,
    },
    cellsEdited: Boolean(table.cellsEdited),
  };
};

export const recipeToPayoutTableColumns = ({
  options = {},
  planning = {},
  cellsEdited = false,
} = {}) => ({
  placesToCover: Math.max(
    1,
    Math.min(50, Number(options.places) || EASY_PLACES)
  ),
  curve: PAYOUT_SHAPES[options.shape] ? options.shape : "standard",
  payoutRatioNumerator:
    Number(options.ratioNumerator) || DEFAULT_RATIO_NUMERATOR,
  payoutRatioDenominator:
    Number(options.ratioDenominator) || DEFAULT_RATIO_DENOMINATOR,
  prizePerEntry: planningFieldFilled(planning.prizePerEntry)
    ? Number(planning.prizePerEntry)
    : null,
  lastPlaceMustGet: (() => {
    const whole = sanitizeWholeDollars(planning.lastPlaceDollars);
    return whole === "" ? null : Number(whole);
  })(),
  roundToWholeDollars: planning.roundToWholeDollars !== false,
  cellsEdited: Boolean(cellsEdited),
});

export const isPayoutPlanningEmpty = (planning = {}) =>
  !planningFieldFilled(planning.prizePerEntry) &&
  !planningFieldFilled(planning.lastPlaceDollars);

export const hasPrizePerEntry = (planning = {}) => {
  const prize = Number(planning.prizePerEntry);
  return (
    planningFieldFilled(planning.prizePerEntry) &&
    Number.isFinite(prize) &&
    prize > 0
  );
};

export const hasLastPlaceFloor = (planning = {}) =>
  hasPrizePerEntry(planning) &&
  isWholePositiveDollar(planning.lastPlaceDollars);

export const isPayoutPlanningComplete = (planning = {}) =>
  hasPrizePerEntry(planning);

export const isPayoutPlanningPartial = (planning = {}) =>
  !isPayoutPlanningEmpty(planning) && !isPayoutPlanningComplete(planning);

export const impliedEntriesForPlaces = (
  quantity,
  numerator = DEFAULT_RATIO_NUMERATOR,
  denominator = DEFAULT_RATIO_DENOMINATOR
) => {
  const q = Number(quantity);
  const num = Math.max(1, Number(numerator) || DEFAULT_RATIO_NUMERATOR);
  const den = Math.max(1, Number(denominator) || DEFAULT_RATIO_DENOMINATOR);
  if (!Number.isFinite(q) || q <= 0) return 0;
  return (q * den) / num;
};

export const prizeFundForColumn = (
  quantity,
  planning,
  numerator,
  denominator
) => {
  if (!isPayoutPlanningComplete(planning)) return 0;
  return (
    impliedEntriesForPlaces(quantity, numerator, denominator) *
    Number(planning.prizePerEntry)
  );
};

export const isRecommendedPayoutOptions = ({
  places,
  shape,
  minLastPercent,
  ratioNumerator,
  ratioDenominator,
} = {}) => {
  const minEmpty = minLastPercent === "" || minLastPercent == null;
  return (
    Number(places) === EASY_PLACES &&
    (shape || "standard") === "standard" &&
    minEmpty &&
    Number(ratioNumerator) === DEFAULT_RATIO_NUMERATOR &&
    Number(ratioDenominator) === DEFAULT_RATIO_DENOMINATOR
  );
};

export const payoutTablesMatch = (left, right) => {
  if (!left || !right || left.length !== right.length) return false;
  const actual = new Map(
    right.map((row) => [
      `${row.payoutQuantity}:${row.payoutOrder}`,
      row.percentage,
    ])
  );
  return left.every((row) => {
    const value = actual.get(`${row.payoutQuantity}:${row.payoutOrder}`);
    return (
      value != null && Math.abs(Number(value) - Number(row.percentage)) < 0.00015
    );
  });
};

export const firstCapStartsAtPlaces = (
  numerator = DEFAULT_RATIO_NUMERATOR,
  denominator = DEFAULT_RATIO_DENOMINATOR
) => {
  const num = Math.max(1, Number(numerator) || DEFAULT_RATIO_NUMERATOR);
  const den = Math.max(1, Number(denominator) || DEFAULT_RATIO_DENOMINATOR);
  return Math.max(1, Math.floor((USBC_FIRST_CAP_ENTRIES * num) / den));
};

export const placesPaidForEntries = (
  entries,
  numerator = DEFAULT_RATIO_NUMERATOR,
  denominator = DEFAULT_RATIO_DENOMINATOR
) => {
  const field = Number(entries);
  const num = Math.max(1, Number(numerator) || DEFAULT_RATIO_NUMERATOR);
  const den = Math.max(1, Number(denominator) || DEFAULT_RATIO_DENOMINATOR);
  if (!Number.isFinite(field) || field <= 0) return 0;
  return Math.max(1, Math.ceil((field * num) / den));
};

export const columnForEntries = (
  entries,
  numerator = DEFAULT_RATIO_NUMERATOR,
  denominator = DEFAULT_RATIO_DENOMINATOR,
  maxPlaces = EASY_PLACES
) => {
  const paid = placesPaidForEntries(entries, numerator, denominator);
  if (paid <= 0) {
    return { paid: 0, highlighted: 0, capped: false };
  }
  const max = Math.max(1, Number(maxPlaces) || EASY_PLACES);
  const highlighted = Math.min(paid, max);
  return { paid, highlighted, capped: paid > max };
};

export const estimatedPrizeFund = (entries, prizePerEntry) => {
  const field = Number(entries);
  const prize = Number(prizePerEntry);
  if (!Number.isFinite(field) || !Number.isFinite(prize) || field <= 0 || prize < 0) {
    return 0;
  }
  return field * prize;
};

export const lastPlaceShare = (payoutData, quantity) => {
  const q = Number(quantity);
  const row = (payoutData || []).find(
    (item) =>
      Number(item.payoutQuantity) === q && Number(item.payoutOrder) === q
  );
  return row == null ? null : Number(row.percentage);
};

export const lastPlaceAmount = (payoutData, quantity, prizeFund) => {
  const share = lastPlaceShare(payoutData, quantity);
  if (share == null) return null;
  return (Number(prizeFund) || 0) * share;
};

export const dollarRoundStep = (roundTo) =>
  Number(roundTo) > 0 ? Number(roundTo) : 1;

export const roundPrizeCash = (amount, roundTo = 1) => {
  const step = dollarRoundStep(roundTo);
  return Math.round(Number(amount || 0) / step) * step;
};

// Nearest dollar of the fund, unless that would go over the fund.
// Then the places total the whole dollars that fit.
export const wholeDollarFundTarget = (prizeFund, roundTo = 1) => {
  const fund = Number(prizeFund) || 0;
  const step = dollarRoundStep(roundTo);
  if (!(fund > 0) || !(step > 0)) return 0;
  const nearest = roundPrizeCash(fund, step);
  if (nearest <= fund + 1e-9) return nearest;
  return Math.floor((fund + 1e-9) / step) * step;
};

export const payoutTotalsMatchFund = (
  totalAllocated,
  prizeFund,
  roundToWholeDollars = true
) => {
  const fund = Number(prizeFund) || 0;
  const total = Number(totalAllocated) || 0;
  if (total > fund + 0.01) return false;
  if (Math.abs(total - fund) <= 0.01) return true;
  if (roundToWholeDollars === false) return false;
  return Math.abs(total - wholeDollarFundTarget(fund)) <= 0.01;
};

export const allocateColumnDollars = ({
  payoutData,
  quantity,
  prizeFund,
  lastPlaceFloor,
  roundTo = 1,
  firstCap,
  ratioNumerator = DEFAULT_RATIO_NUMERATOR,
  ratioDenominator = DEFAULT_RATIO_DENOMINATOR,
} = {}) => {
  const q = Number(quantity);
  const fund = Number(prizeFund) || 0;
  const step = dollarRoundStep(roundTo);
  if (!q || !(fund > 0)) return [];

  const rows = (payoutData || [])
    .filter(
      (row) =>
        Number(row.payoutQuantity) === q &&
        Number(row.payoutOrder) >= 1 &&
        Number(row.payoutOrder) <= q
    )
    .sort((a, b) => a.payoutOrder - b.payoutOrder);

  if (!rows.length) return [];

  const capShare =
    firstCap != null && Number(firstCap) > 0
      ? Number(firstCap)
      : firstPlaceCapForPlaces(q, ratioNumerator, ratioDenominator);
  const firstCapDollars = Math.floor((fund * capShare) / step) * step;

  const amounts = rows.map((row) =>
    roundPrizeCash(fund * Number(row.percentage), step)
  );
  if (amounts[0] > firstCapDollars) {
    amounts[0] = firstCapDollars;
  }

  const n = amounts.length;
  const last = n - 1;
  const floorCash = roundPrizeCash(lastPlaceFloor, step);
  const target = wholeDollarFundTarget(fund, step);
  const allocated = amounts.reduce((sum, amount) => sum + amount, 0);
  const leftoverSteps = Math.round((target - allocated) / step);

  const canAdd = (index) => {
    const next = amounts[index] + step;
    if (index === 0) return next <= firstCapDollars;
    return next <= amounts[index - 1];
  };
  const canSubtract = (index) => {
    const next = amounts[index] - step;
    if (next < 0) return false;
    if (index === last && floorCash > 0 && next < floorCash) return false;
    if (index < last && next < amounts[index + 1]) return false;
    return true;
  };

  const adding = leftoverSteps > 0;
  let left = Math.abs(leftoverSteps);
  let cursor = 0;
  let idle = 0;
  while (left > 0 && idle < n) {
    const ok = adding ? canAdd(cursor) : canSubtract(cursor);
    if (ok) {
      amounts[cursor] += adding ? step : -step;
      left -= 1;
      idle = 0;
    } else {
      idle += 1;
    }
    cursor = (cursor + 1) % n;
  }

  const takeFromFirst = (need) => {
    if (!(need > 0) || amounts.length < 2) return 0;
    const slack = Math.max(0, amounts[0] - amounts[1]);
    const take = Math.min(need, Math.floor(slack / step) * step);
    amounts[0] -= take;
    return take;
  };

  if (floorCash > 0 && amounts[last] < floorCash) {
    amounts[last] += takeFromFirst(floorCash - amounts[last]);
  }
  for (let place = last - 1; place >= 1; place -= 1) {
    if (amounts[place] >= amounts[place + 1]) continue;
    amounts[place] += takeFromFirst(amounts[place + 1] - amounts[place]);
  }

  return rows.map((row, index) => ({
    place: Number(row.payoutOrder),
    amount: amounts[index],
  }));
};

export const allocateExactCents = (amounts, totalPrizeFund) => {
  const roundedAmounts = (amounts || []).map((amount) => roundCurrency(amount));
  const currentTotal = roundedAmounts.reduce(
    (sum, amount) => addCurrency(sum, amount),
    0
  );
  const fund = roundCurrency(Number(totalPrizeFund) || 0);
  const difference = roundCurrency(fund - currentTotal);
  if (Math.abs(difference) < 0.01) return roundedAmounts;

  const amountsWithIndex = roundedAmounts.map((amount, index) => ({
    index,
    amount,
    decimalPart: amount - Math.floor(amount),
  }));
  amountsWithIndex.sort((a, b) => {
    if (difference > 0) return a.decimalPart - b.decimalPart;
    return b.decimalPart - a.decimalPart;
  });

  const adjustedAmounts = [...roundedAmounts];
  const lastIndex = amountsWithIndex[amountsWithIndex.length - 1].index;
  adjustedAmounts[lastIndex] = roundCurrency(
    addCurrency(adjustedAmounts[lastIndex], difference)
  );
  return adjustedAmounts;
};

const rowsForPlaceDollars = ({ places, payoutData, quantity }) => {
  if (places?.length) {
    return [...places]
      .map((row) => ({
        place: Number(row.place),
        percentage: Number(row.percentage),
      }))
      .sort((a, b) => a.place - b.place);
  }
  const q = Number(quantity);
  return (payoutData || [])
    .filter(
      (row) =>
        Number(row.payoutQuantity) === q &&
        Number(row.payoutOrder) >= 1 &&
        Number(row.payoutOrder) <= q
    )
    .sort((a, b) => a.payoutOrder - b.payoutOrder)
    .map((row) => ({
      place: Number(row.payoutOrder),
      percentage: Number(row.percentage),
    }));
};

export const materializePlaceAmounts = ({
  places,
  payoutData,
  quantity,
  prizeFund,
  roundToWholeDollars = true,
  lastPlaceFloor,
  firstCap,
  ratioNumerator,
  ratioDenominator,
  roundTo = 1,
} = {}) => {
  const fund = Number(prizeFund) || 0;
  const rows = rowsForPlaceDollars({ places, payoutData, quantity });
  if (!rows.length || !(fund > 0)) {
    return rows.map((row) => ({
      place: row.place,
      percentage: Number.isFinite(row.percentage) ? row.percentage : 0,
      amount: 0,
    }));
  }

  if (roundToWholeDollars === false) {
    const amounts = allocateExactCents(
      rows.map((row) => fund * row.percentage),
      fund
    );
    return rows.map((row, index) => ({
      place: row.place,
      amount: amounts[index],
      percentage: row.percentage,
    }));
  }

  const q = rows.length;
  const synthetic = rows.map((row, index) => ({
    payoutQuantity: q,
    payoutOrder: index + 1,
    percentage: row.percentage,
  }));
  const allocated = allocateColumnDollars({
    payoutData: synthetic,
    quantity: q,
    prizeFund: fund,
    lastPlaceFloor,
    roundTo,
    firstCap,
    ratioNumerator,
    ratioDenominator,
  });
  return rows.map((row, index) => ({
    place: row.place,
    amount: allocated[index]?.amount ?? 0,
    percentage: row.percentage,
  }));
};

export const dollarAmountForPlace = (allocated, order) => {
  const row = (allocated || []).find(
    (item) => Number(item.place) === Number(order)
  );
  return row == null ? null : row.amount;
};

export const displayedDollarAmount = (amount) =>
  Math.round(Number(amount) || 0);

export const lastPlacePercentToMeetFloor = (prizeFund, floor) => {
  const fund = Number(prizeFund);
  const dollars = Number(floor);
  if (!(fund > 0) || !(dollars > 0)) return 0;
  return Math.min(1, Math.ceil((dollars / fund) * PERCENT_SCALE - 1e-12) / PERCENT_SCALE);
};

export const maxLastPlacePercent = (places, firstCap = 1) => {
  const n = Math.max(1, Number(places) || 1);
  if (n === 1) return 1;
  const cap = Math.max(0, Number(firstCap) || 1);
  const equal = 1 / n;
  if (equal <= cap + 1e-15) return equal;
  return Math.min(cap, (1 - Math.min(cap, 1)) / (n - 1));
};

export const columnMissesLastPlaceFloor = (
  payoutData,
  quantity,
  planning,
  numerator,
  denominator,
  prizeFundOverride
) => {
  if (!hasLastPlaceFloor(planning)) return false;
  const floor = roundPrizeCash(planning.lastPlaceDollars, 1);
  const fund =
    Number(prizeFundOverride) > 0
      ? Number(prizeFundOverride)
      : prizeFundForColumn(
          quantity,
          planning,
          numerator,
          denominator
        );
  const allocated = allocateColumnDollars({
    payoutData,
    quantity,
    prizeFund: fund,
    lastPlaceFloor: floor,
    ratioNumerator: numerator,
    ratioDenominator: denominator,
  });
  const last = dollarAmountForPlace(allocated, quantity);
  if (last == null) return false;
  return last < floor;
};

export const firstPlaceCapForPlaces = (places, numerator, denominator) =>
  places >= firstCapStartsAtPlaces(numerator, denominator)
    ? FIRST_PLACE_CAP
    : 1;

export const columnPercentDisplay = (total) =>
  (Number(total) * 100).toFixed(1);

export const isColumnPercentValid = (total) =>
  Math.abs(Number(total) - 1) < 0.0005;

export const generateShapedPayoutTable = ({
  maxPlaces = 15,
  shape = "standard",
  minLastPercent = null,
  lastPlaceDollars = null,
  prizePerEntry = null,
  payoutRatioNumerator = DEFAULT_RATIO_NUMERATOR,
  payoutRatioDenominator = DEFAULT_RATIO_DENOMINATOR,
} = {}) => {
  const safeMax = Math.max(1, Math.min(50, Number(maxPlaces) || 15));
  const safeShape = PAYOUT_SHAPES[shape] ? shape : "standard";
  const tableData = [];

  for (let quantity = 1; quantity <= safeMax; quantity += 1) {
    const minLast = minLastPercentForColumn({
      quantity,
      minLastPercent,
      lastPlaceDollars,
      prizePerEntry,
      payoutRatioNumerator,
      payoutRatioDenominator,
    });
    const percents = percentsForQuantity(
      quantity,
      safeShape,
      minLast,
      firstPlaceCapForPlaces(
        quantity,
        payoutRatioNumerator,
        payoutRatioDenominator
      )
    );
    percents.forEach((percentage, index) => {
      tableData.push({
        payoutQuantity: quantity,
        payoutOrder: index + 1,
        percentage,
      });
    });
  }

  return tableData;
};

export const buildPayoutTableFromRecipe = (
  { places, shape, minLastPercent, ratioNumerator, ratioDenominator } = {},
  planning = DEFAULT_PAYOUT_PLANNING
) => {
  const applyFloor = hasLastPlaceFloor(planning);
  return generateShapedPayoutTable({
    maxPlaces: Math.max(1, Math.min(50, Number(places) || 15)),
    shape: shape || "standard",
    minLastPercent:
      minLastPercent === "" || minLastPercent == null
        ? null
        : Number(minLastPercent) / 100,
    lastPlaceDollars: applyFloor ? planning.lastPlaceDollars : null,
    prizePerEntry: hasPrizePerEntry(planning) ? planning.prizePerEntry : null,
    payoutRatioNumerator: Number(ratioNumerator) || DEFAULT_RATIO_NUMERATOR,
    payoutRatioDenominator:
      Number(ratioDenominator) || DEFAULT_RATIO_DENOMINATOR,
  });
};

export const buildRecommendedPayoutTable = () =>
  generateShapedPayoutTable({
    maxPlaces: EASY_PLACES,
    shape: "standard",
    payoutRatioNumerator: DEFAULT_RATIO_NUMERATOR,
    payoutRatioDenominator: DEFAULT_RATIO_DENOMINATOR,
  });

const csvCell = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text) || /^[=+\-@]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const percentDisplay = (percentage) =>
  (Math.round(Number(percentage) * 10000) / 100).toFixed(2);

const formatExportDollar = (amount, dollarStyle) => {
  if (amount == null || amount === "") return "";
  return dollarStyle === "currency" ? `$${amount}` : String(amount);
};

export const buildPayoutExportTable = ({
  payoutData = [],
  quantity = null,
  planning = DEFAULT_PAYOUT_PLANNING,
  ratioNumerator = DEFAULT_RATIO_NUMERATOR,
  ratioDenominator = DEFAULT_RATIO_DENOMINATOR,
  prizeFund,
  prizeFundForQuantity,
  includeDollars,
  headerStyle = "csv",
  dollarStyle = "plain",
} = {}) => {
  const dollarsOn =
    includeDollars ?? isPayoutPlanningComplete(planning);
  const sheet = headerStyle === "sheet";
  const orders = [
    ...new Set((payoutData || []).map((row) => Number(row.payoutOrder))),
  ].sort((a, b) => a - b);

  if (quantity != null && Number(quantity) > 0) {
    const qty = Number(quantity);
    const fund = dollarsOn
      ? Number(prizeFund) > 0
        ? Number(prizeFund)
        : prizeFundForColumn(qty, planning, ratioNumerator, ratioDenominator)
      : 0;
    const allocated = dollarsOn
      ? allocateColumnDollars({
          payoutData,
          quantity: qty,
          prizeFund: fund,
          lastPlaceFloor: planning.lastPlaceDollars,
          ratioNumerator,
          ratioDenominator,
        })
      : [];
    const headers = dollarsOn
      ? ["Place", "Percent", "Dollars"]
      : ["Place", "Percent"];
    const rows = [];
    for (let order = 1; order <= qty; order += 1) {
      const row = (payoutData || []).find(
        (item) =>
          Number(item.payoutQuantity) === qty &&
          Number(item.payoutOrder) === order
      );
      if (!row) continue;
      const cells = [order, percentDisplay(row.percentage)];
      if (dollarsOn) {
        cells.push(
          formatExportDollar(dollarAmountForPlace(allocated, order), dollarStyle)
        );
      }
      rows.push(cells);
    }
    return {
      kind: "column",
      quantity: qty,
      includeDollars: dollarsOn,
      headers,
      rows,
    };
  }

  const quantities = [
    ...new Set((payoutData || []).map((row) => Number(row.payoutQuantity))),
  ].sort((a, b) => a - b);
  const headers = ["Place"];
  quantities.forEach((qty) => {
    headers.push(sheet ? String(qty) : `${qty} places %`);
    if (dollarsOn) {
      headers.push(sheet ? `${qty} $` : `${qty} places $`);
    }
  });
  const funds = {};
  const dollars = {};
  if (dollarsOn) {
    quantities.forEach((qty) => {
      const fund =
        typeof prizeFundForQuantity === "function"
          ? Number(prizeFundForQuantity(qty)) || 0
          : prizeFundForColumn(
              qty,
              planning,
              ratioNumerator,
              ratioDenominator
            );
      funds[qty] = fund;
      dollars[qty] = allocateColumnDollars({
        payoutData,
        quantity: qty,
        prizeFund: fund,
        lastPlaceFloor: planning.lastPlaceDollars,
        ratioNumerator,
        ratioDenominator,
      });
    });
  }
  const rows = orders.map((order) => {
    const cells = [order];
    quantities.forEach((qty) => {
      if (order > qty) {
        cells.push("");
        if (dollarsOn) cells.push("");
        return;
      }
      const row = (payoutData || []).find(
        (item) =>
          Number(item.payoutQuantity) === qty &&
          Number(item.payoutOrder) === order
      );
      cells.push(row ? percentDisplay(row.percentage) : "");
      if (dollarsOn) {
        const cash =
          funds[qty] > 0 ? dollarAmountForPlace(dollars[qty], order) : "";
        cells.push(formatExportDollar(cash, dollarStyle));
      }
    });
    return cells;
  });
  return {
    kind: "grid",
    quantity: null,
    includeDollars: dollarsOn,
    headers,
    rows,
  };
};

export const buildPayoutTableCsv = (options = {}) => {
  const table = buildPayoutExportTable({
    ...options,
    headerStyle: "csv",
    dollarStyle: "plain",
  });
  const lines = [table.headers, ...table.rows].map((row) =>
    row.map(csvCell).join(",")
  );
  return `${lines.join("\n")}\n`;
};

export const downloadPayoutTableCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const INFER_RATIOS = [
  [DEFAULT_RATIO_NUMERATOR, DEFAULT_RATIO_DENOMINATOR],
  [1, 4],
  [1, 5],
  [1, 8],
  [1, 10],
];

export const inferPayoutOptions = (payoutData) => {
  if (!payoutData?.length) {
    return {
      options: { ...RECOMMENDED_PAYOUT_OPTIONS },
      cellsEdited: false,
    };
  }

  const maxPlaces = Math.max(
    ...payoutData.map((row) => Number(row.payoutQuantity) || 0)
  );
  const shapes = Object.keys(PAYOUT_SHAPES);

  for (const shape of shapes) {
    for (const [numerator, denominator] of INFER_RATIOS) {
      const options = {
        places: String(maxPlaces),
        shape,
        minLastPercent: "",
        ratioNumerator: String(numerator),
        ratioDenominator: String(denominator),
      };
      const withoutFloor = generateShapedPayoutTable({
        maxPlaces,
        shape,
        payoutRatioNumerator: numerator,
        payoutRatioDenominator: denominator,
      });
      if (payoutTablesMatch(payoutData, withoutFloor)) {
        return { options, cellsEdited: false };
      }
      const generated = generateShapedPayoutTable({
        maxPlaces,
        shape,
        payoutRatioNumerator: numerator,
        payoutRatioDenominator: denominator,
        lastPlaceDollars: DEFAULT_LAST_PLACE_DOLLARS,
        prizePerEntry: DEFAULT_PRIZE_PER_ENTRY,
      });
      if (payoutTablesMatch(payoutData, generated)) {
        return { options, cellsEdited: false };
      }
    }
  }

  return {
    options: {
      ...RECOMMENDED_PAYOUT_OPTIONS,
      places: String(maxPlaces),
    },
    cellsEdited: true,
  };
};

export const recipeForLoadedTable = (table, payoutData) => {
  const saved = recipeFromPayoutTable(table);
  if (saved) return saved;
  const inferred = inferPayoutOptions(payoutData);
  return {
    options: inferred.options,
    planning: { ...DEFAULT_PAYOUT_PLANNING },
    cellsEdited: inferred.cellsEdited,
  };
};

export const roundDollarAmount = (amount, roundTo) => {
  if (!amount && amount !== 0) return 0;
  if (!roundTo || roundTo <= 0) {
    return Math.round(amount * 100) / 100;
  }
  return Math.round(amount / roundTo) * roundTo;
};

export const previewColumnDollars = ({
  payoutData,
  quantity,
  prizeFund,
  lastPlaceFloor,
  roundTo = 1,
  firstCap,
  ratioNumerator,
  ratioDenominator,
}) => {
  const allocated = allocateColumnDollars({
    payoutData,
    quantity,
    prizeFund,
    lastPlaceFloor,
    roundTo,
    firstCap,
    ratioNumerator,
    ratioDenominator,
  });
  return allocated.map((item) => {
    const row = (payoutData || []).find(
      (entry) =>
        Number(entry.payoutQuantity) === Number(quantity) &&
        Number(entry.payoutOrder) === item.place
    );
    return {
      place: item.place,
      percentage: row ? row.percentage : null,
      amount: item.amount,
    };
  });
};

function percentsForQuantity(places, shape, minLastPercent, firstCap) {
  let percents;

  if (places === 1) {
    percents = [1];
  } else if (places === 2) {
    percents = fixSum([2 / 3, 1 / 3]);
  } else if (shape === "even") {
    percents = evenPercents(places, firstCap);
  } else if (shape === "standard" && places === 15) {
    percents = fixSum([...USBC_SAMPLE_15]);
  } else {
    percents = topHeavyPercents(places, shape, firstCap);
  }

  return applyMinLast(
    finalizePercents(percents, firstCap),
    minLastPercent,
    firstCap
  );
}

function topHeavyPercents(places, shape, firstCap) {
  const first = firstPlaceShare(places, shape, firstCap);
  const second = first / 2;
  const remaining = Math.max(0, 1 - first - second);
  const tailCount = places - 2;
  const tail =
    tailCount <= 0
      ? []
      : geometricPercents(tailCount, FIRST_LAST_RATIO.standard).map(
          (value) => value * remaining
        );
  return capFirstPlace(fixSum([first, second, ...tail]), firstCap);
}

function evenPercents(places, firstCap) {
  const first = firstPlaceShare(places, "even", firstCap);
  const rest = geometricPercents(places - 1, FIRST_LAST_RATIO.even).map(
    (value) => value * (1 - first)
  );
  return capFirstPlace(fixSum([first, ...rest]), firstCap);
}

function finalizePercents(percents, firstCap) {
  let next = percents;
  for (let step = 0; step < 6; step += 1) {
    next = ensureDecreasing(next, firstCap);
    next = capFirstPlace(next, firstCap);
  }
  return fixSum(ensureDecreasing(next, firstCap));
}

function ensureDecreasing(percents, firstCap = 1) {
  if (!percents.length) return percents;
  const next = percents.slice();
  for (let i = 1; i < next.length; i += 1) {
    if (next[i] > next[i - 1]) next[i] = next[i - 1];
  }

  let leftover =
    1 - next.reduce((sum, value) => sum + value, 0);
  for (let i = 0; i < next.length && leftover > 0.0000001; i += 1) {
    const maxAllowed = i === 0 ? firstCap : next[i - 1];
    const room = Math.max(0, maxAllowed - next[i]);
    const add = Math.min(room, leftover);
    next[i] += add;
    leftover -= add;
  }

  return next;
}

function normalizeMinLast(minLastPercent) {
  if (minLastPercent == null || minLastPercent === "") return null;
  const value = Number(minLastPercent);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value > 1 ? value / 100 : value;
}

function minLastPercentForColumn({
  quantity,
  minLastPercent,
  lastPlaceDollars,
  prizePerEntry,
  payoutRatioNumerator,
  payoutRatioDenominator,
} = {}) {
  const dollarFloor = Number(lastPlaceDollars);
  const prize = Number(prizePerEntry);
  if (
    Number.isFinite(dollarFloor) &&
    dollarFloor > 0 &&
    Number.isFinite(prize) &&
    prize > 0
  ) {
    const fund =
      impliedEntriesForPlaces(
        quantity,
        payoutRatioNumerator,
        payoutRatioDenominator
      ) * prize;
    if (fund > 0) {
      return lastPlacePercentToMeetFloor(fund, dollarFloor);
    }
  }
  return normalizeMinLast(minLastPercent);
}

const MIN_PLACE_STEP = 0.0001;
const VISIBLE_PLACE_STEP = 0.001;

function applyMinLast(percents, minLastPercent, firstCap) {
  if (!minLastPercent || percents.length === 0) return percents;
  const n = percents.length;
  const target = Math.min(
    minLastPercent,
    maxLastPlacePercent(n, firstCap)
  );
  if (target <= 0) return percents;

  const next = percents.slice();
  next[n - 1] = Math.max(next[n - 1], target);
  return finalizeWithLastFloor(next, target, firstCap);
}

function staircaseMin(targetLast, step, placesFromLast) {
  return targetLast + placesFromLast * step;
}

function stackFromLast(percents, targetLast, step) {
  const n = percents.length;
  const next = percents.slice();
  next[n - 1] = Math.max(next[n - 1], targetLast);
  for (let i = n - 2; i >= 0; i -= 1) {
    const minAllowed = next[i + 1] + step;
    if (next[i] < minAllowed) next[i] = minAllowed;
  }
  return next;
}

function rebalanceStaircase(percents, targetLast, step, firstCap) {
  const n = percents.length;
  const next = percents.slice();
  const minAt = (index) => staircaseMin(targetLast, step, n - 1 - index);

  let extra = next.reduce((sum, value) => sum + value, 0) - 1;
  if (extra > 1e-12) {
    for (let i = 0; i < n - 1 && extra > 1e-12; i += 1) {
      const take = Math.min(Math.max(0, next[i] - minAt(i)), extra);
      next[i] -= take;
      extra -= take;
    }
    if (extra > 1e-12) return null;
  }

  let short = 1 - next.reduce((sum, value) => sum + value, 0);
  if (short > 1e-12) {
    for (let i = 0; i < n - 1 && short > 1e-12; i += 1) {
      const maxAllowed = i === 0 ? firstCap : next[i - 1] - step;
      const add = Math.min(Math.max(0, maxAllowed - next[i]), short);
      next[i] += add;
      short -= add;
    }
    if (short > 1e-12) return null;
  }

  if (next[0] > firstCap + 1e-12) return null;
  for (let i = 1; i < n; i += 1) {
    if (next[i] > next[i - 1] - step + 1e-12 && step > 0) {
      next[i] = Math.min(next[i], next[i - 1] - step);
    }
  }
  if (next[n - 1] < targetLast - 1e-12) return null;
  return next;
}

function finalizeWithLastFloor(percents, targetLast, firstCap) {
  const stepsToTry = [VISIBLE_PLACE_STEP, MIN_PLACE_STEP, 0];
  for (const step of stepsToTry) {
    const stacked = stackFromLast(percents, targetLast, step);
    if (stacked[0] > firstCap + 1e-12) continue;
    const balanced = rebalanceStaircase(
      stacked,
      targetLast,
      step,
      firstCap
    );
    if (!balanced) continue;
    return roundPercentsKeepLast(balanced, targetLast, firstCap, step);
  }

  const tied = stackFromLast(percents, targetLast, 0);
  return roundPercentsKeepLast(tied, targetLast, firstCap, 0);
}

function roundToPercent(value) {
  return Math.round(value * PERCENT_SCALE) / PERCENT_SCALE;
}

function roundPercentsKeepLast(percents, targetLast, firstCap, step = 0) {
  const n = percents.length;
  const rounded = percents.map((value) => roundToPercent(value));
  const minLast = Math.ceil(targetLast * PERCENT_SCALE - 1e-12) / PERCENT_SCALE;
  if (rounded[n - 1] < minLast) {
    rounded[n - 1] = minLast;
  }

  const minAt = (index) =>
    roundToPercent(staircaseMin(minLast, step, n - 1 - index));

  let drift = roundToPercent(
    1 - rounded.reduce((sum, value) => sum + value, 0)
  );

  for (let i = 0; i < n - 1 && drift !== 0; i += 1) {
    if (drift > 0) {
      const maxAllowed = i === 0 ? firstCap : roundToPercent(rounded[i - 1] - step);
      const add = Math.min(
        Math.max(0, roundToPercent(maxAllowed - rounded[i])),
        drift
      );
      if (add <= 0) continue;
      rounded[i] = roundToPercent(rounded[i] + add);
      drift = roundToPercent(drift - add);
    } else {
      const take = Math.min(
        Math.max(0, roundToPercent(rounded[i] - minAt(i))),
        -drift
      );
      if (take <= 0) continue;
      rounded[i] = roundToPercent(rounded[i] - take);
      drift = roundToPercent(drift + take);
    }
  }

  if (drift !== 0) {
    rounded[n - 1] = roundToPercent(rounded[n - 1] + drift);
  }

  return rounded;
}

function firstPlaceShare(places, shape, firstCap) {
  const at15 =
    shape === "first" ? 0.38 : shape === "even" ? 0.22 : 0.3014;
  const share = at15 * Math.pow(15 / Math.max(places, 1), 0.35);
  return Math.min(firstCap, 2 / 3, Math.max(0.12, share));
}

function geometricPercents(places, firstLastRatio) {
  const ratio = Math.pow(1 / firstLastRatio, 1 / (places - 1));
  const weights = [];
  for (let i = 0; i < places; i += 1) {
    weights.push(Math.pow(ratio, i));
  }
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  return weights.map((weight) => weight / total);
}

function capFirstPlace(percents, cap) {
  if (!percents.length || percents[0] <= cap) return percents;
  const first = cap;
  const restOld = percents.slice(1).reduce((sum, value) => sum + value, 0);
  const restNew = 1 - first;
  if (restOld <= 0) return percents;
  const next = percents.map((value, index) =>
    index === 0 ? first : value * (restNew / restOld)
  );
  return fixSum(next);
}

function fixSum(percents) {
  if (!percents.length) return percents;
  const total = percents.reduce((sum, value) => sum + value, 0);
  if (total === 0) return percents;
  const scaled = percents.map((value) => value / total);
  const rounded = scaled.map((value) => Math.round(value * 10000) / 10000);
  const roundedTotal = rounded.reduce((sum, value) => sum + value, 0);
  const drift = Math.round((1 - roundedTotal) * 10000) / 10000;
  if (drift === 0) return rounded;
  const last = rounded.length - 1;
  rounded[last] = Math.round((rounded[last] + drift) * 10000) / 10000;
  if (rounded[last] < 0) {
    rounded[last] = 0;
  }
  return rounded;
}
